export enum SignalingState {
  Connecting = "connecting",

  Connected = "connected",

  Disconnected = "disconnected",
}

export type SignalingMessageCallback = (data: Record<string, unknown>) => void;

export type SignalingStateCallback = (state: SignalingState) => void;

function generateDeviceId(): string {
  const bytes = new Uint8Array(16);

  crypto.getRandomValues(bytes);

  bytes[6] = (bytes[6] & 0x0f) | 0x40;

  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    "",
  );

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const DEVICE_ID_KEY = "@Guest/deviceId";

function getOrCreateDeviceId(): string {
  const stored = localStorage.getItem(DEVICE_ID_KEY);

  if (stored) return stored;

  const id = generateDeviceId();

  localStorage.setItem(DEVICE_ID_KEY, id);

  return id;
}

/**
 * Returns the persistent device UUID for this guest.
 * Creates and saves one to localStorage if it doesn't exist yet.
 */

export function getGuestDeviceId(): string {
  return getOrCreateDeviceId();
}

let sharedDeviceId: string | null = null;

export class SignalingClient {
  private ws: WebSocket | null = null;

  readonly deviceId: string;

  // Heartbeat

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  private lastPong: number = 0;

  private lastPingSent: number = 0;

  private _latencyMs = 0;

  private static readonly HEARTBEAT_INTERVAL = 10_000;

  private static readonly HEARTBEAT_TIMEOUT = 30_000;

  // Auto-reconnect (infinite until intentional disconnect)

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  private reconnectAttempts = 0;

  private _reconnectCountdown = 0;

  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  // Connection validation

  private connectionResolve: ((value: boolean) => void) | null = null;

  private connectionTimeout: ReturnType<typeof setTimeout> | null = null;

  // Saved params

  savedHost: string | null = null;

  savedPort: string | null = null;

  savedLabel: string | null = null;

  // Backend-broker params (only set when mode === "backend-broker")

  private _mode: "direct" | "backend-broker" = "direct";

  private savedSignalingUrl: string | null = null;

  private savedTicket: string | null = null;

  private savedRomId: string | null = null;

  get mode(): "direct" | "backend-broker" {
    return this._mode;
  }

  // Callbacks

  onMessage: SignalingMessageCallback | null = null;

  onStateChanged: SignalingStateCallback | null = null;

  onReconnectNeeded: (() => void) | null = null;

  private _connected = false;

  get connected(): boolean {
    return this._connected;
  }

  get latencyMs(): number {
    return this._latencyMs;
  }

  get reconnectCountdown(): number {
    return this._reconnectCountdown;
  }

  get reconnectAttempt(): number {
    return this.reconnectAttempts;
  }

  private intentionalDisconnect = false;

  constructor(deviceId?: string) {
    this.deviceId =
      deviceId ?? sharedDeviceId ?? (sharedDeviceId = getOrCreateDeviceId());
  }

  async connect(opts: {
    host: string;

    port: string;

    label: string;

    video?: boolean;

    audio?: boolean;

    mode?: "direct" | "backend-broker";

    signalingUrl?: string;

    ticket?: string;

    romId?: string;
  }): Promise<boolean> {
    const {
      host,

      port,

      label,

      video = false,

      audio = false,

      mode = "direct",

      signalingUrl,

      ticket,

      romId,
    } = opts;

    this.savedHost = host;

    this.savedPort = port;

    this.savedLabel = label;

    this._mode = mode;

    if (mode === "backend-broker") {
      if (!signalingUrl || !ticket) return false;

      this.savedSignalingUrl = signalingUrl;

      this.savedTicket = ticket;

      this.savedRomId = romId ?? this.savedRomId;
    }

    if (this._connected) return true;

    if (mode === "direct" && (!host || !port)) return false;

    this.intentionalDisconnect = false;

    this.onStateChanged?.(SignalingState.Connecting);

    try {
      return await new Promise<boolean>((resolve) => {
        this.connectionResolve = resolve;

        const url =
          mode === "backend-broker"
            ? `${signalingUrl}?romId=${encodeURIComponent(
                this.savedRomId ?? "",
              )}&ticket=${encodeURIComponent(ticket ?? "")}`
            : `ws://${host}:${port}/webrtc`;

        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
          // Kero requires a register frame regardless of how the WS reaches it.

          // In broker mode the backend passes it through and the host bridge

          // forwards it to kero, so we send it just like in direct mode.

          this.sendRegister({ label, video, audio });

          if (mode === "backend-broker") {
            this.completeConnection(true);
          }
        };

        this.ws.onmessage = (event) => {
          this.handleRawMessage(event.data);
        };

        this.ws.onerror = () => {
          this.setDisconnected();

          this.completeConnection(false);

          this.attemptReconnect();
        };

        this.ws.onclose = () => {
          const wasConnected = this._connected;

          this.setDisconnected();

          this.completeConnection(false);

          if (!this.intentionalDisconnect) this.attemptReconnect();
          else if (wasConnected) {
            /* intentional, no reconnect */
          }
        };

        this.connectionTimeout = setTimeout(() => {
          this.completeConnection(false);

          this.attemptReconnect();
        }, 5000);
      });
    } catch {
      this.clearConnectionTimeout();

      return false;
    }
  }

  async connectToLobby(opts: {
    host: string;

    port: string;

    label: string;
  }): Promise<boolean> {
    const { host, port, label } = opts;

    this.savedHost = host;

    this.savedPort = port;

    this.savedLabel = label;

    if (this._connected) return true;

    if (!host || !port) return false;

    this.intentionalDisconnect = false;

    this.onStateChanged?.(SignalingState.Connecting);

    try {
      return await new Promise<boolean>((resolve) => {
        this.connectionResolve = resolve;

        this.ws = new WebSocket(`ws://${host}:${port}/webrtc`);

        this.ws.onopen = () => {
          this.send({
            type: "lobby_join",

            deviceId: this.deviceId,

            label: label || "Eriol Guest",

            platform: this.detectPlatform(),
          });
        };

        this.ws.onmessage = (event) => this.handleRawMessage(event.data);

        this.ws.onerror = () => {
          this.setDisconnected();

          this.completeConnection(false);
        };

        this.ws.onclose = () => {
          this.setDisconnected();

          this.completeConnection(false);
        };

        this.connectionTimeout = setTimeout(
          () => this.completeConnection(false),

          5000,
        );
      });
    } catch {
      this.clearConnectionTimeout();

      return false;
    }
  }

  async disconnect(): Promise<void> {
    this.intentionalDisconnect = true;

    this.stopHeartbeat();

    this.stopCountdown();

    this.clearReconnectTimer();

    this.clearConnectionTimeout();

    this.closeSocket();

    this.setDisconnected();
  }

  async closeTransport(): Promise<void> {
    this.stopHeartbeat();

    this.closeSocket();

    this.setDisconnected();
  }

  send(payload: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  sendRegister(opts: { label: string; video: boolean; audio: boolean }): void {
    this.send({
      type: "register",

      deviceId: this.deviceId,

      label: opts.label || "Eriol Guest",

      tracks: { video: opts.video, audio: opts.audio },
    });
  }

  sendChat(text: string): void {
    this.send({
      type: "chat",

      deviceId: this.deviceId,

      text,

      timestamp: Date.now(),
    });
  }

  dispose(): void {
    this.stopHeartbeat();

    this.stopCountdown();

    this.clearReconnectTimer();

    this.clearConnectionTimeout();

    this.closeSocket();
  }

  // --- Private ---

  private handleRawMessage(raw: string): void {
    let data: Record<string, unknown>;

    try {
      data = JSON.parse(raw);
    } catch {
      return;
    }

    const type = data.type as string;

    if (type === "registered" || type === "lobby_joined") {
      this.completeConnection(true);

      this.onMessage?.(data);

      return;
    }

    // Legacy: any message confirms connection

    if (this.connectionResolve) {
      this.completeConnection(true);
    }

    if (type === "pong") {
      const now = Date.now();

      if (this.lastPingSent) {
        this._latencyMs = now - this.lastPingSent;
      }

      this.lastPong = now;

      this.reconnectAttempts = 0;

      return;
    }

    this.onMessage?.(data);
  }

  private completeConnection(success: boolean): void {
    if (this.connectionResolve) {
      const resolve = this.connectionResolve;

      this.connectionResolve = null;

      this.clearConnectionTimeout();

      if (success) {
        this._connected = true;

        this.reconnectAttempts = 0;

        this.startHeartbeat();

        this.onStateChanged?.(SignalingState.Connected);
      }

      resolve(success);
    }
  }

  private setDisconnected(): void {
    const wasConnected = this._connected;

    this._connected = false;

    if (wasConnected) {
      this.onStateChanged?.(SignalingState.Disconnected);
    }
  }

  private closeSocket(): void {
    try {
      this.ws?.close();
    } catch {
      /* ignore */
    }

    this.ws = null;
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.lastPong = Date.now();

    this.heartbeatTimer = setInterval(() => {
      if (!this._connected) {
        this.stopHeartbeat();

        return;
      }

      if (
        this.lastPong &&
        Date.now() - this.lastPong > SignalingClient.HEARTBEAT_TIMEOUT
      ) {
        this.stopHeartbeat();

        this.attemptReconnect();

        return;
      }

      this.lastPingSent = Date.now();

      this.send({
        type: "ping",

        deviceId: this.deviceId,

        timestamp: this.lastPingSent,
      });
    }, SignalingClient.HEARTBEAT_INTERVAL);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);

      this.heartbeatTimer = null;
    }
  }

  private attemptReconnect(): void {
    if (this.intentionalDisconnect) return;

    if (this.reconnectTimer) return;

    if (this._mode === "backend-broker") {
      if (!this.savedSignalingUrl || !this.savedTicket || !this.savedLabel)
        return;
    } else if (!this.savedHost || !this.savedPort || !this.savedLabel) {
      return;
    }

    // Exponential backoff capped at 30s: 1s, 2s, 4s, 8s, 16s, 30s, 30s, ...

    const delaySec = Math.min(2 ** this.reconnectAttempts, 30);

    this._reconnectCountdown = delaySec;

    this.stopCountdown();

    this.countdownTimer = setInterval(() => {
      this._reconnectCountdown = Math.max(0, this._reconnectCountdown - 1);

      this.onStateChanged?.(SignalingState.Disconnected); // tick to update UI
    }, 1000);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;

      this.stopCountdown();

      this._reconnectCountdown = 0;

      this.reconnectAttempts++;

      this.onReconnectNeeded?.();
    }, delaySec * 1000);
  }

  private stopCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);

      this.countdownTimer = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);

      this.reconnectTimer = null;
    }
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);

      this.connectionTimeout = null;
    }
  }

  private detectPlatform(): string {
    const ua = navigator.userAgent.toLowerCase();

    if (ua.includes("android")) return "android";

    if (ua.includes("iphone") || ua.includes("ipad")) return "ios";

    if (ua.includes("mac")) return "macos";

    if (ua.includes("windows")) return "windows";

    if (ua.includes("linux")) return "linux";

    return "web";
  }
}
